import { SupportedLanguage } from "@/services/languageService";

type PokedexTranslationKey =
  | "pokedexEyebrow"
  | "pokedexPageTitle"
  | "pokedexPageSubtitle"
  | "pokedexSearchLabel"
  | "pokedexSearchPlaceholder"
  | "pokedexAllGenerations"
  | "pokedexAllTypes"
  | "pokedexSortLabel"
  | "pokedexSortNumber"
  | "pokedexSortName"
  | "pokedexSortRarity"
  | "pokedexSortHeight"
  | "pokedexSortWeight"
  | "pokedexClearFilters"
  | "pokedexTotalEntries"
  | "pokedexFilteredEntries"
  | "pokedexPageIndicator"
  | "pokedexInspect"
  | "pokedexShowingResults"
  | "pokedexPrevious"
  | "pokedexNext"
  | "pokedexPageStatus"
  | "pokedexEmptyTitle"
  | "pokedexEmptyBody"
  | "pokedexLoadErrorTitle"
  | "pokedexLoadErrorBody"
  | "pokedexDetailDescription"
  | "pokedexDetailError"
  | "pokedexDetailLoading"
  | "pokedexOverviewTitle"
  | "pokedexBaseExperience"
  | "pokedexAbilitiesTitle"
  | "pokedexStatsTitle"
  | "pokedexMovesTitle"
  | "pokedexMovesLearnset"
  | "pokedexMovesUnavailable"
  | "pokedexMovesPower"
  | "pokedexMovesAccuracy"
  | "pokedexMovesPP"
  | "pokedexMovesPriority"
  | "pokedexMovesEffect"
  | "pokedexMovesEffectFallback"
  | "pokedexVersionGroupScarletViolet"
  | "pokedexMovesRoutes"
  | "pokedexMovesLevel"
  | "pokedexMoveMethodLevelUp"
  | "pokedexMoveMethodMachine"
  | "pokedexMoveMethodEgg"
  | "pokedexMoveMethodTutor"
  | "pokedexMoveMethodEvolution"
  | "pokedexMoveMethodFormChange"
  | "pokedexMoveMethodSpecial"
  | "pokedexMoveDamagePhysical"
  | "pokedexMoveDamageSpecial"
  | "pokedexMoveDamageStatus";

type PokedexTranslationRecord = Record<PokedexTranslationKey, string>;

const EN_BASE: PokedexTranslationRecord = {
  pokedexEyebrow: "Pokémon Database",
  pokedexPageTitle: "Pokédex",
  pokedexPageSubtitle:
    "Search every Pokémon, filter by type or generation, and open a richer detail view without leaving the page.",
  pokedexSearchLabel: "Search",
  pokedexSearchPlaceholder: "Search by name or Pokédex number",
  pokedexAllGenerations: "All Generations",
  pokedexAllTypes: "All Types",
  pokedexSortLabel: "Sort",
  pokedexSortNumber: "Lowest Number",
  pokedexSortName: "Name A-Z",
  pokedexSortRarity: "Highest Rarity",
  pokedexSortHeight: "Tallest First",
  pokedexSortWeight: "Heaviest First",
  pokedexClearFilters: "Clear Filters",
  pokedexTotalEntries: "Total Entries",
  pokedexFilteredEntries: "Matches",
  pokedexPageIndicator: "Page",
  pokedexInspect: "Inspect",
  pokedexShowingResults: "Showing {{start}}-{{end}} of {{total}} entries",
  pokedexPrevious: "Previous",
  pokedexNext: "Next",
  pokedexPageStatus: "Page {{page}} / {{total}}",
  pokedexEmptyTitle: "No Pokémon matched this search",
  pokedexEmptyBody: "Try a different name, generation, or type filter.",
  pokedexLoadErrorTitle: "Pokédex data could not be loaded",
  pokedexLoadErrorBody: "The local Pokémon database was unavailable, so the Pokédex cannot render right now.",
  pokedexDetailDescription: "Detailed Pokédex entry",
  pokedexDetailError: "Unable to load this Pokémon entry right now.",
  pokedexDetailLoading: "Loading Pokédex entry...",
  pokedexOverviewTitle: "Overview",
  pokedexBaseExperience: "Base Experience",
  pokedexAbilitiesTitle: "Abilities",
  pokedexStatsTitle: "Base Stats",
  pokedexMovesTitle: "Featured Moves",
  pokedexMovesLearnset: "Learnset",
  pokedexMovesUnavailable: "Move details were unavailable for this entry.",
  pokedexMovesPower: "Power",
  pokedexMovesAccuracy: "Accuracy",
  pokedexMovesPP: "PP",
  pokedexMovesPriority: "Priority",
  pokedexMovesEffect: "Effect",
  pokedexMovesEffectFallback: "No effect text was available for this move in the current language.",
  pokedexVersionGroupScarletViolet: "Scarlet / Violet",
  pokedexMovesRoutes: "Learn Routes",
  pokedexMovesLevel: "Level",
  pokedexMoveMethodLevelUp: "Level Up",
  pokedexMoveMethodMachine: "Machine",
  pokedexMoveMethodEgg: "Egg",
  pokedexMoveMethodTutor: "Tutor",
  pokedexMoveMethodEvolution: "Evolution",
  pokedexMoveMethodFormChange: "Form Change",
  pokedexMoveMethodSpecial: "Special",
  pokedexMoveDamagePhysical: "Physical",
  pokedexMoveDamageSpecial: "Special",
  pokedexMoveDamageStatus: "Status",
};

export const pokedexTranslations: Record<SupportedLanguage, PokedexTranslationRecord> = {
  en: EN_BASE,
  es: {
    ...EN_BASE,
    pokedexEyebrow: "Base de Datos Pokémon",
    pokedexPageTitle: "Pokédex",
    pokedexPageSubtitle: "Busca cualquier Pokémon, filtra por tipo o generacion y abre una vista detallada sin salir de la pagina.",
    pokedexSearchLabel: "Buscar",
    pokedexSearchPlaceholder: "Buscar por nombre o numero de Pokedex",
    pokedexAllGenerations: "Todas las generaciones",
    pokedexAllTypes: "Todos los tipos",
    pokedexSortLabel: "Ordenar",
    pokedexSortNumber: "Numero mas bajo",
    pokedexSortName: "Nombre A-Z",
    pokedexSortRarity: "Mayor rareza",
    pokedexSortHeight: "Mas altos primero",
    pokedexSortWeight: "Mas pesados primero",
    pokedexClearFilters: "Limpiar filtros",
    pokedexTotalEntries: "Total",
    pokedexFilteredEntries: "Coincidencias",
    pokedexPageIndicator: "Pagina",
    pokedexInspect: "Ver detalles",
    pokedexShowingResults: "Mostrando {{start}}-{{end}} de {{total}} entradas",
    pokedexOverviewTitle: "Resumen",
    pokedexBaseExperience: "Experiencia base",
    pokedexAbilitiesTitle: "Habilidades",
    pokedexStatsTitle: "Estadisticas base",
    pokedexMovesTitle: "Movimientos destacados",
    pokedexMovesLearnset: "Aprendizaje",
    pokedexMovesUnavailable: "Los detalles de movimientos no estaban disponibles para esta entrada.",
    pokedexMovesPower: "Potencia",
    pokedexMovesAccuracy: "Precisión",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "Prioridad",
    pokedexMovesEffect: "Efecto",
    pokedexMovesRoutes: "Métodos",
    pokedexMovesLevel: "Nivel",
    pokedexMoveMethodLevelUp: "Subida de nivel",
    pokedexMoveMethodMachine: "Máquina",
    pokedexMoveMethodEgg: "Huevo",
    pokedexMoveMethodTutor: "Tutor",
    pokedexMoveMethodEvolution: "Evolución",
    pokedexMoveMethodFormChange: "Cambio de forma",
    pokedexMoveMethodSpecial: "Especial",
    pokedexMoveDamagePhysical: "Físico",
    pokedexMoveDamageSpecial: "Especial",
    pokedexMoveDamageStatus: "Estado",
    pokedexMovesEffectFallback: "No hay texto de efecto disponible para este movimiento en el idioma actual.",
  },
  fr: {
    ...EN_BASE,
    pokedexEyebrow: "Base de données Pokémon",
    pokedexPageTitle: "Pokédex",
    pokedexPageSubtitle:
      "Recherchez chaque Pokémon, filtrez par type ou generation, et ouvrez une vue detaillee sans quitter la page.",
    pokedexSearchLabel: "Recherche",
    pokedexSearchPlaceholder: "Rechercher par nom ou numero de Pokedex",
    pokedexAllGenerations: "Toutes les generations",
    pokedexAllTypes: "Tous les types",
    pokedexSortLabel: "Trier",
    pokedexSortNumber: "Numero croissant",
    pokedexSortName: "Nom A-Z",
    pokedexSortRarity: "Rarete maximale",
    pokedexSortHeight: "Du plus grand au plus petit",
    pokedexSortWeight: "Du plus lourd au plus leger",
    pokedexClearFilters: "Effacer les filtres",
    pokedexTotalEntries: "Total",
    pokedexFilteredEntries: "Resultats",
    pokedexPageIndicator: "Page",
    pokedexInspect: "Voir",
    pokedexShowingResults: "Affichage de {{start}} à {{end}} sur {{total}} entrées",
    pokedexOverviewTitle: "Aperçu",
    pokedexBaseExperience: "Expérience de base",
    pokedexAbilitiesTitle: "Talents",
    pokedexStatsTitle: "Statistiques de base",
    pokedexMovesTitle: "Capacités vedettes",
    pokedexMovesLearnset: "Apprentissage",
    pokedexMovesUnavailable: "Les détails des attaques n’étaient pas disponibles pour cette entrée.",
    pokedexMovesPower: "Puissance",
    pokedexMovesAccuracy: "Précision",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "Priorité",
    pokedexMovesEffect: "Effet",
    pokedexMovesRoutes: "Méthodes",
    pokedexMovesLevel: "Niveau",
    pokedexMoveMethodLevelUp: "Niveau",
    pokedexMoveMethodMachine: "CT/CS",
    pokedexMoveMethodEgg: "Œuf",
    pokedexMoveMethodTutor: "Maître des capacités",
    pokedexMoveMethodEvolution: "Évolution",
    pokedexMoveMethodFormChange: "Changement de forme",
    pokedexMoveMethodSpecial: "Spécial",
    pokedexMoveDamagePhysical: "Physique",
    pokedexMoveDamageSpecial: "Spécial",
    pokedexMoveDamageStatus: "Statut",
    pokedexMovesEffectFallback: "Aucun texte d’effet n’est disponible pour cette attaque dans la langue actuelle.",
  },
  pt: {
    ...EN_BASE,
    pokedexEyebrow: "Banco de Dados Pokémon",
    pokedexPageTitle: "Pokédex",
    pokedexPageSubtitle:
      "Pesquise qualquer Pokémon, filtre por tipo ou geracao e abra uma visao detalhada sem sair da pagina.",
    pokedexSearchLabel: "Pesquisar",
    pokedexSearchPlaceholder: "Pesquisar por nome ou numero da Pokedex",
    pokedexAllGenerations: "Todas as geracoes",
    pokedexAllTypes: "Todos os tipos",
    pokedexSortLabel: "Ordenar",
    pokedexSortNumber: "Menor numero",
    pokedexSortName: "Nome A-Z",
    pokedexSortRarity: "Maior raridade",
    pokedexSortHeight: "Mais altos primeiro",
    pokedexSortWeight: "Mais pesados primeiro",
    pokedexClearFilters: "Limpar filtros",
    pokedexTotalEntries: "Total",
    pokedexFilteredEntries: "Correspondencias",
    pokedexPageIndicator: "Pagina",
    pokedexInspect: "Ver detalhes",
    pokedexShowingResults: "Mostrando {{start}}-{{end}} de {{total}} entradas",
    pokedexOverviewTitle: "Visão geral",
    pokedexBaseExperience: "Experiência base",
    pokedexAbilitiesTitle: "Habilidades",
    pokedexStatsTitle: "Estatisticas base",
    pokedexMovesTitle: "Movimentos em destaque",
    pokedexMovesLearnset: "Aprendizado",
    pokedexMovesUnavailable: "Os detalhes dos movimentos não estavam disponíveis para esta entrada.",
    pokedexMovesPower: "Potência",
    pokedexMovesAccuracy: "Precisão",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "Prioridade",
    pokedexMovesEffect: "Efeito",
    pokedexMovesRoutes: "Formas",
    pokedexMovesLevel: "Nível",
    pokedexMoveMethodLevelUp: "Subir de nível",
    pokedexMoveMethodMachine: "Máquina",
    pokedexMoveMethodEgg: "Ovo",
    pokedexMoveMethodTutor: "Tutor",
    pokedexMoveMethodEvolution: "Evolução",
    pokedexMoveMethodFormChange: "Mudança de forma",
    pokedexMoveMethodSpecial: "Especial",
    pokedexMoveDamagePhysical: "Físico",
    pokedexMoveDamageSpecial: "Especial",
    pokedexMoveDamageStatus: "Status",
    pokedexMovesEffectFallback: "Nao ha texto de efeito disponivel para este movimento no idioma atual.",
  },
  ar: {
    ...EN_BASE,
    pokedexEyebrow: "قاعدة بيانات بوكيمون",
    pokedexPageTitle: "بوكيديكس",
    pokedexPageSubtitle: "ابحث عن كل بوكيمون، صفّ حسب النوع أو الجيل، وافتح عرضا تفصيليا بدون مغادرة الصفحة.",
    pokedexSearchLabel: "بحث",
    pokedexSearchPlaceholder: "ابحث بالاسم أو رقم البوكيديكس",
    pokedexAllGenerations: "كل الأجيال",
    pokedexAllTypes: "كل الأنواع",
    pokedexSortLabel: "ترتيب",
    pokedexSortNumber: "الأصغر رقما",
    pokedexSortName: "الاسم أ-ي",
    pokedexSortRarity: "الأعلى ندرة",
    pokedexSortHeight: "الأطول أولا",
    pokedexSortWeight: "الأثقل أولا",
    pokedexClearFilters: "مسح الفلاتر",
    pokedexTotalEntries: "الإجمالي",
    pokedexFilteredEntries: "النتائج",
    pokedexPageIndicator: "الصفحة",
    pokedexInspect: "عرض التفاصيل",
    pokedexShowingResults: "عرض {{start}}-{{end}} من أصل {{total}}",
    pokedexOverviewTitle: "نظرة عامة",
    pokedexBaseExperience: "الخبرة الأساسية",
    pokedexAbilitiesTitle: "القدرات",
    pokedexStatsTitle: "الإحصائيات الأساسية",
    pokedexMovesTitle: "الحركات المميزة",
    pokedexMovesLearnset: "قائمة التعلّم",
    pokedexMovesUnavailable: "تفاصيل الحركات غير متاحة لهذه البطاقة.",
    pokedexMovesPower: "القوة",
    pokedexMovesAccuracy: "الدقة",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "الأولوية",
    pokedexMovesEffect: "التأثير",
    pokedexMovesRoutes: "طرق التعلّم",
    pokedexMovesLevel: "المستوى",
    pokedexMoveMethodLevelUp: "رفع المستوى",
    pokedexMoveMethodMachine: "آلة",
    pokedexMoveMethodEgg: "بيضة",
    pokedexMoveMethodTutor: "معلّم",
    pokedexMoveMethodEvolution: "تطوّر",
    pokedexMoveMethodFormChange: "تغيير الشكل",
    pokedexMoveMethodSpecial: "خاص",
    pokedexMoveDamagePhysical: "جسدي",
    pokedexMoveDamageSpecial: "خاص",
    pokedexMoveDamageStatus: "حالة",
    pokedexMovesEffectFallback: "لا يوجد وصف للتأثير لهذه الحركة باللغة الحالية.",
  },
  de: {
    ...EN_BASE,
    pokedexEyebrow: "Pokémon-Datenbank",
    pokedexPageTitle: "Pokédex",
    pokedexPageSubtitle:
      "Durchsuche jedes Pokémon, filtere nach Typ oder Generation und oeffne eine Detailansicht ohne die Seite zu verlassen.",
    pokedexSearchLabel: "Suche",
    pokedexSearchPlaceholder: "Nach Name oder Pokedex-Nummer suchen",
    pokedexAllGenerations: "Alle Generationen",
    pokedexAllTypes: "Alle Typen",
    pokedexSortLabel: "Sortieren",
    pokedexSortNumber: "Niedrigste Nummer",
    pokedexSortName: "Name A-Z",
    pokedexSortRarity: "Hoechste Seltenheit",
    pokedexSortHeight: "Groesste zuerst",
    pokedexSortWeight: "Schwerste zuerst",
    pokedexClearFilters: "Filter zuruecksetzen",
    pokedexTotalEntries: "Gesamt",
    pokedexFilteredEntries: "Treffer",
    pokedexPageIndicator: "Seite",
    pokedexInspect: "Details",
    pokedexShowingResults: "Zeige {{start}}-{{end}} von {{total}} Einträgen",
    pokedexOverviewTitle: "Überblick",
    pokedexBaseExperience: "Basis-Erfahrung",
    pokedexAbilitiesTitle: "Fähigkeiten",
    pokedexStatsTitle: "Basiswerte",
    pokedexMovesTitle: "Ausgewaehlte Attacken",
    pokedexMovesLearnset: "Erlernbar",
    pokedexMovesUnavailable: "Attackendetails waren für diesen Eintrag nicht verfügbar.",
    pokedexMovesPower: "Stärke",
    pokedexMovesAccuracy: "Genauigkeit",
    pokedexMovesPP: "AP",
    pokedexMovesPriority: "Priorität",
    pokedexMovesEffect: "Effekt",
    pokedexMovesRoutes: "Erlernmethoden",
    pokedexMovesLevel: "Level",
    pokedexMoveMethodLevelUp: "Levelaufstieg",
    pokedexMoveMethodMachine: "TM",
    pokedexMoveMethodEgg: "Ei",
    pokedexMoveMethodTutor: "Tutor",
    pokedexMoveMethodEvolution: "Entwicklung",
    pokedexMoveMethodFormChange: "Formwechsel",
    pokedexMoveMethodSpecial: "Spezial",
    pokedexMoveDamagePhysical: "Physisch",
    pokedexMoveDamageSpecial: "Spezial",
    pokedexMoveDamageStatus: "Status",
    pokedexMovesEffectFallback: "Kein Effekttext fuer diese Attacke in der aktuellen Sprache verfuegbar.",
  },
  ja: {
    ...EN_BASE,
    pokedexEyebrow: "ポケモンデータベース",
    pokedexPageTitle: "ポケモン図鑑",
    pokedexPageSubtitle: "すべてのポケモンを検索し、タイプや世代で絞り込み、ページを離れずに詳細を確認できます。",
    pokedexSearchLabel: "検索",
    pokedexSearchPlaceholder: "名前または図鑑番号で検索",
    pokedexAllGenerations: "すべての世代",
    pokedexAllTypes: "すべてのタイプ",
    pokedexSortLabel: "並び替え",
    pokedexSortNumber: "番号が小さい順",
    pokedexSortName: "名前順",
    pokedexSortRarity: "希少度が高い順",
    pokedexSortHeight: "高さが高い順",
    pokedexSortWeight: "重さが重い順",
    pokedexClearFilters: "フィルターをクリア",
    pokedexTotalEntries: "総数",
    pokedexFilteredEntries: "一致",
    pokedexPageIndicator: "ページ",
    pokedexInspect: "詳細",
    pokedexShowingResults: "{{total}}件中 {{start}}-{{end}} を表示",
    pokedexOverviewTitle: "概要",
    pokedexBaseExperience: "基礎経験値",
    pokedexAbilitiesTitle: "とくせい",
    pokedexStatsTitle: "種族値",
    pokedexMovesTitle: "おすすめわざ",
    pokedexMovesLearnset: "習得",
    pokedexMovesUnavailable: "このエントリの技詳細は利用できません。",
    pokedexMovesPower: "威力",
    pokedexMovesAccuracy: "命中",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "優先度",
    pokedexMovesEffect: "効果",
    pokedexMovesRoutes: "習得方法",
    pokedexMovesLevel: "レベル",
    pokedexMoveMethodLevelUp: "レベルアップ",
    pokedexMoveMethodMachine: "わざマシン",
    pokedexMoveMethodEgg: "タマゴ",
    pokedexMoveMethodTutor: "おしえわざ",
    pokedexMoveMethodEvolution: "進化",
    pokedexMoveMethodFormChange: "フォルムチェンジ",
    pokedexMoveMethodSpecial: "特殊",
    pokedexMoveDamagePhysical: "物理",
    pokedexMoveDamageSpecial: "特殊",
    pokedexMoveDamageStatus: "変化",
    pokedexMovesEffectFallback: "この技の効果説明は、現在の言語では利用できません。",
  },
  ko: {
    ...EN_BASE,
    pokedexEyebrow: "포켓몬 데이터베이스",
    pokedexPageTitle: "포켓몬 도감",
    pokedexPageSubtitle: "모든 포켓몬을 검색하고, 타입 또는 세대로 필터링하고, 페이지를 벗어나지 않고 상세 정보를 확인하세요.",
    pokedexSearchLabel: "검색",
    pokedexSearchPlaceholder: "이름 또는 도감 번호로 검색",
    pokedexAllGenerations: "모든 세대",
    pokedexAllTypes: "모든 타입",
    pokedexSortLabel: "정렬",
    pokedexSortNumber: "번호 오름차순",
    pokedexSortName: "이름순",
    pokedexSortRarity: "희귀도 높은 순",
    pokedexSortHeight: "키 큰 순",
    pokedexSortWeight: "몸무게 무거운 순",
    pokedexClearFilters: "필터 초기화",
    pokedexTotalEntries: "전체",
    pokedexFilteredEntries: "일치",
    pokedexPageIndicator: "페이지",
    pokedexInspect: "상세 보기",
    pokedexOverviewTitle: "개요",
    pokedexStatsTitle: "기본 능력치",
    pokedexMovesTitle: "대표 기술",
    pokedexMovesLearnset: "기술 습득",
    pokedexMoveMethodTutor: "가르침 기술",
    pokedexMovesEffectFallback: "현재 언어로 이 기술의 효과 설명을 찾을 수 없습니다.",
  },
  "zh-CN": {
    ...EN_BASE,
    pokedexEyebrow: "宝可梦数据库",
    pokedexPageTitle: "宝可梦图鉴",
    pokedexPageSubtitle: "可以按名称、编号、世代或属性快速查找，并在当前页直接查看更完整的宝可梦详情。",
    pokedexSearchLabel: "搜索",
    pokedexSearchPlaceholder: "按名称或图鉴编号搜索",
    pokedexAllGenerations: "全部世代",
    pokedexAllTypes: "全部属性",
    pokedexSortLabel: "排序",
    pokedexSortNumber: "编号从小到大",
    pokedexSortName: "名称 A-Z",
    pokedexSortRarity: "稀有度从高到低",
    pokedexSortHeight: "身高从高到低",
    pokedexSortWeight: "体重从高到低",
    pokedexClearFilters: "清空筛选",
    pokedexTotalEntries: "总条目",
    pokedexFilteredEntries: "匹配数",
    pokedexPageIndicator: "页码",
    pokedexInspect: "查看详情",
    pokedexShowingResults: "当前显示 {{start}}-{{end}}，共 {{total}} 条",
    pokedexOverviewTitle: "概览",
    pokedexBaseExperience: "基础经验",
    pokedexAbilitiesTitle: "特性",
    pokedexLoadErrorTitle: "图鉴数据加载失败",
    pokedexLoadErrorBody: "本地宝可梦数据库暂时无法读取，所以图鉴页无法渲染。",
    pokedexStatsTitle: "种族值",
    pokedexMovesTitle: "代表招式",
    pokedexMovesLearnset: "习得信息",
    pokedexMovesUnavailable: "暂时无法提供该条目的招式详情。",
    pokedexMovesPower: "威力",
    pokedexMovesAccuracy: "命中",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "优先度",
    pokedexMovesEffect: "效果",
    pokedexMovesRoutes: "习得方式",
    pokedexMovesLevel: "等级",
    pokedexMoveMethodLevelUp: "升级",
    pokedexMoveMethodMachine: "招式机",
    pokedexMoveMethodEgg: "遗传",
    pokedexMoveMethodTutor: "教学招式",
    pokedexMoveMethodEvolution: "进化",
    pokedexMoveMethodFormChange: "形态变化",
    pokedexMoveMethodSpecial: "特殊",
    pokedexMoveDamagePhysical: "物理",
    pokedexMoveDamageSpecial: "特殊",
    pokedexMoveDamageStatus: "变化",
    pokedexMoveMethodTutor: "教学招式",
    pokedexMovesEffectFallback: "当前语言暂无该招式的效果说明。",
  },
  "zh-TW": {
    ...EN_BASE,
    pokedexEyebrow: "寶可夢資料庫",
    pokedexPageTitle: "寶可夢圖鑑",
    pokedexPageSubtitle: "可依名稱、編號、世代或屬性快速查找，並在同一頁直接查看更完整的寶可夢詳情。",
    pokedexSearchLabel: "搜尋",
    pokedexSearchPlaceholder: "以名稱或圖鑑編號搜尋",
    pokedexAllGenerations: "全部世代",
    pokedexAllTypes: "全部屬性",
    pokedexSortLabel: "排序",
    pokedexSortNumber: "編號由小到大",
    pokedexSortName: "名稱 A-Z",
    pokedexSortRarity: "稀有度由高到低",
    pokedexSortHeight: "身高由高到低",
    pokedexSortWeight: "體重由高到低",
    pokedexClearFilters: "清除篩選",
    pokedexTotalEntries: "總數",
    pokedexFilteredEntries: "符合數",
    pokedexPageIndicator: "頁數",
    pokedexInspect: "查看詳情",
    pokedexShowingResults: "目前顯示 {{start}}-{{end}}，共 {{total}} 筆",
    pokedexOverviewTitle: "概覽",
    pokedexBaseExperience: "基礎經驗",
    pokedexAbilitiesTitle: "特性",
    pokedexLoadErrorTitle: "圖鑑資料載入失敗",
    pokedexLoadErrorBody: "本地寶可夢資料庫暫時無法讀取，因此圖鑑頁無法顯示。",
    pokedexStatsTitle: "種族值",
    pokedexMovesTitle: "代表招式",
    pokedexMovesLearnset: "習得資訊",
    pokedexMovesUnavailable: "暫時無法提供此條目的招式詳情。",
    pokedexMovesPower: "威力",
    pokedexMovesAccuracy: "命中",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "優先度",
    pokedexMovesEffect: "效果",
    pokedexMovesRoutes: "習得方式",
    pokedexMovesLevel: "等級",
    pokedexMoveMethodLevelUp: "升級",
    pokedexMoveMethodMachine: "招式機",
    pokedexMoveMethodEgg: "遺傳",
    pokedexMoveMethodTutor: "教學招式",
    pokedexMoveMethodEvolution: "進化",
    pokedexMoveMethodFormChange: "形態變化",
    pokedexMoveMethodSpecial: "特殊",
    pokedexMoveDamagePhysical: "物理",
    pokedexMoveDamageSpecial: "特殊",
    pokedexMoveDamageStatus: "變化",
    pokedexMoveMethodTutor: "教學招式",
    pokedexMovesEffectFallback: "目前語言沒有此招式的效果說明。",
  },
  ru: {
    ...EN_BASE,
    pokedexEyebrow: "База данных Покемон",
    pokedexPageTitle: "Покедекс",
    pokedexPageSubtitle:
      "Ищите любого Покемона, фильтруйте по типу или поколению и открывайте подробный просмотр, не покидая страницу.",
    pokedexSearchLabel: "Поиск",
    pokedexSearchPlaceholder: "Поиск по имени или номеру Покедекса",
    pokedexAllGenerations: "Все поколения",
    pokedexAllTypes: "Все типы",
    pokedexSortLabel: "Сортировка",
    pokedexSortNumber: "Сначала меньший номер",
    pokedexSortName: "Имя A-Z",
    pokedexSortRarity: "Самые редкие",
    pokedexSortHeight: "Сначала выше",
    pokedexSortWeight: "Сначала тяжелее",
    pokedexClearFilters: "Сбросить фильтры",
    pokedexTotalEntries: "Всего",
    pokedexFilteredEntries: "Совпадения",
    pokedexPageIndicator: "Страница",
    pokedexInspect: "Подробнее",
    pokedexShowingResults: "Показано {{start}}-{{end}} из {{total}}",
    pokedexOverviewTitle: "Обзор",
    pokedexBaseExperience: "Базовый опыт",
    pokedexAbilitiesTitle: "Способности",
    pokedexStatsTitle: "Базовые характеристики",
    pokedexMovesTitle: "Избранные приемы",
    pokedexMovesLearnset: "Набор",
    pokedexMovesUnavailable: "Детали приёмов недоступны для этой записи.",
    pokedexMovesPower: "Сила",
    pokedexMovesAccuracy: "Точность",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "Приоритет",
    pokedexMovesEffect: "Эффект",
    pokedexMovesRoutes: "Способы",
    pokedexMovesLevel: "Уровень",
    pokedexMoveMethodLevelUp: "Уровень",
    pokedexMoveMethodMachine: "Машина",
    pokedexMoveMethodEgg: "Яйцо",
    pokedexMoveMethodTutor: "Наставник",
    pokedexMoveMethodEvolution: "Эволюция",
    pokedexMoveMethodFormChange: "Смена формы",
    pokedexMoveMethodSpecial: "Особое",
    pokedexMoveDamagePhysical: "Физ.",
    pokedexMoveDamageSpecial: "Спец.",
    pokedexMoveDamageStatus: "Статус",
    pokedexMovesEffectFallback: "Нет описания эффекта для этого приема на текущем языке.",
  },
  id: {
    ...EN_BASE,
    pokedexEyebrow: "Database Pokémon",
    pokedexPageTitle: "Pokédex",
    pokedexPageSubtitle:
      "Cari semua Pokémon, saring berdasarkan tipe atau generasi, lalu buka tampilan detail tanpa meninggalkan halaman.",
    pokedexSearchLabel: "Cari",
    pokedexSearchPlaceholder: "Cari berdasarkan nama atau nomor Pokedex",
    pokedexAllGenerations: "Semua Generasi",
    pokedexAllTypes: "Semua Tipe",
    pokedexSortLabel: "Urutkan",
    pokedexSortNumber: "Nomor terendah",
    pokedexSortName: "Nama A-Z",
    pokedexSortRarity: "Kelangkaan tertinggi",
    pokedexSortHeight: "Paling tinggi dulu",
    pokedexSortWeight: "Paling berat dulu",
    pokedexClearFilters: "Hapus Filter",
    pokedexTotalEntries: "Total",
    pokedexFilteredEntries: "Cocok",
    pokedexPageIndicator: "Halaman",
    pokedexInspect: "Lihat Detail",
    pokedexShowingResults: "Menampilkan {{start}}-{{end}} dari {{total}} entri",
    pokedexOverviewTitle: "Ringkasan",
    pokedexBaseExperience: "Pengalaman dasar",
    pokedexAbilitiesTitle: "Kemampuan",
    pokedexStatsTitle: "Statistik dasar",
    pokedexMovesTitle: "Gerakan unggulan",
    pokedexMovesLearnset: "Learnset",
    pokedexMovesUnavailable: "Detail gerakan tidak tersedia untuk entri ini.",
    pokedexMovesPower: "Kekuatan",
    pokedexMovesAccuracy: "Akurasi",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "Prioritas",
    pokedexMovesEffect: "Efek",
    pokedexMovesRoutes: "Metode",
    pokedexMovesLevel: "Level",
    pokedexMoveMethodLevelUp: "Naik level",
    pokedexMoveMethodMachine: "Mesin",
    pokedexMoveMethodEgg: "Telur",
    pokedexMoveMethodTutor: "Tutor",
    pokedexMoveMethodEvolution: "Evolusi",
    pokedexMoveMethodFormChange: "Ubah bentuk",
    pokedexMoveMethodSpecial: "Spesial",
    pokedexMoveDamagePhysical: "Fisik",
    pokedexMoveDamageSpecial: "Spesial",
    pokedexMoveDamageStatus: "Status",
    pokedexMovesEffectFallback: "Tidak ada deskripsi efek untuk jurus ini dalam bahasa saat ini.",
  },
  tr: {
    ...EN_BASE,
    pokedexEyebrow: "Pokémon Veritabanı",
    pokedexPageTitle: "Pokédex",
    pokedexPageSubtitle:
      "Tum Pokémonlari ara, tur veya nesle gore filtrele ve sayfadan ayrilmadan ayrintili gorunumu ac.",
    pokedexSearchLabel: "Ara",
    pokedexSearchPlaceholder: "Isim veya Pokedex numarasi ile ara",
    pokedexAllGenerations: "Tum Nesiller",
    pokedexAllTypes: "Tum Turler",
    pokedexSortLabel: "Sirala",
    pokedexSortNumber: "En kucuk numara",
    pokedexSortName: "Isim A-Z",
    pokedexSortRarity: "En yuksek nadirlik",
    pokedexSortHeight: "En uzun once",
    pokedexSortWeight: "En agir once",
    pokedexClearFilters: "Filtreleri Temizle",
    pokedexTotalEntries: "Toplam",
    pokedexFilteredEntries: "Eslesen",
    pokedexPageIndicator: "Sayfa",
    pokedexInspect: "Incele",
    pokedexShowingResults: "{{total}} girdiden {{start}}-{{end}} gosteriliyor",
    pokedexOverviewTitle: "Genel bakış",
    pokedexBaseExperience: "Temel deneyim",
    pokedexAbilitiesTitle: "Yetenekler",
    pokedexStatsTitle: "Temel istatistikler",
    pokedexMovesTitle: "One cikan hamleler",
    pokedexMovesLearnset: "Ogrenme seti",
    pokedexMovesUnavailable: "Bu giriş için hamle ayrıntıları mevcut değil.",
    pokedexMovesPower: "Güç",
    pokedexMovesAccuracy: "İsabet",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "Öncelik",
    pokedexMovesEffect: "Etki",
    pokedexMovesRoutes: "Ogrenme yollari",
    pokedexMovesLevel: "Seviye",
    pokedexMoveMethodLevelUp: "Seviye atlama",
    pokedexMoveMethodMachine: "Makine",
    pokedexMoveMethodEgg: "Yumurta",
    pokedexMoveMethodTutor: "Egitmen",
    pokedexMoveMethodEvolution: "Evrim",
    pokedexMoveMethodFormChange: "Form degisimi",
    pokedexMoveMethodSpecial: "Ozel",
    pokedexMoveDamagePhysical: "Fiziksel",
    pokedexMoveDamageSpecial: "Ozel",
    pokedexMoveDamageStatus: "Durum",
    pokedexMovesEffectFallback: "Bu hareket icin mevcut dilde etki metni bulunamadi.",
  },
  vi: {
    ...EN_BASE,
    pokedexEyebrow: "Cơ sở dữ liệu Pokémon",
    pokedexPageTitle: "Pokédex",
    pokedexPageSubtitle:
      "Tim tat ca Pokémon, loc theo he hoac the he, va mo xem chi tiet ma khong can roi khoi trang.",
    pokedexSearchLabel: "Tim kiem",
    pokedexSearchPlaceholder: "Tim theo ten hoac so Pokedex",
    pokedexAllGenerations: "Tat ca the he",
    pokedexAllTypes: "Tat ca he",
    pokedexSortLabel: "Sap xep",
    pokedexSortNumber: "So nho nhat",
    pokedexSortName: "Ten A-Z",
    pokedexSortRarity: "Do hiem cao nhat",
    pokedexSortHeight: "Cao nhat truoc",
    pokedexSortWeight: "Nang nhat truoc",
    pokedexClearFilters: "Xoa bo loc",
    pokedexTotalEntries: "Tong so",
    pokedexFilteredEntries: "Khop",
    pokedexPageIndicator: "Trang",
    pokedexInspect: "Xem chi tiet",
    pokedexShowingResults: "Đang hiển thị {{start}}-{{end}} / {{total}}",
    pokedexOverviewTitle: "Tổng quan",
    pokedexBaseExperience: "Kinh nghiệm cơ bản",
    pokedexAbilitiesTitle: "Khả năng",
    pokedexStatsTitle: "Chỉ số cơ bản",
    pokedexMovesTitle: "Chiêu thức nổi bật",
    pokedexMovesLearnset: "Học chiêu",
    pokedexMovesUnavailable: "Không có chi tiết chiêu thức cho mục này.",
    pokedexMovesPower: "Sức mạnh",
    pokedexMovesAccuracy: "Chính xác",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "Ưu tiên",
    pokedexMovesEffect: "Hiệu ứng",
    pokedexMovesRoutes: "Cách học",
    pokedexMovesLevel: "Cấp",
    pokedexMoveMethodLevelUp: "Lên cấp",
    pokedexMoveMethodMachine: "Máy",
    pokedexMoveMethodEgg: "Trứng",
    pokedexMoveMethodTutor: "Dạy chiêu",
    pokedexMoveMethodEvolution: "Tiến hoá",
    pokedexMoveMethodFormChange: "Đổi dạng",
    pokedexMoveMethodSpecial: "Đặc biệt",
    pokedexMoveDamagePhysical: "Vật lý",
    pokedexMoveDamageSpecial: "Đặc biệt",
    pokedexMoveDamageStatus: "Trạng thái",
    pokedexMovesEffectFallback: "Khong co mo ta hieu ung cho chieu nay trong ngon ngu hien tai.",
  },
  hi: {
    ...EN_BASE,
    pokedexEyebrow: "पोकेमोन डेटाबेस",
    pokedexPageTitle: "Pokédex",
    pokedexPageSubtitle:
      "हर पोकेमोन को खोजें, टाइप या पीढ़ी से फ़िल्टर करें, और पेज छोड़े बिना विस्तृत विवरण देखें।",
    pokedexSearchLabel: "Khoj",
    pokedexSearchPlaceholder: "Naam ya Pokedex number se khojen",
    pokedexAllGenerations: "Sabhi generations",
    pokedexAllTypes: "Sabhi types",
    pokedexSortLabel: "Sort",
    pokedexSortNumber: "Sabse chhota number",
    pokedexSortName: "Name A-Z",
    pokedexSortRarity: "Sabse zyada rarity",
    pokedexSortHeight: "Sabse lamba pehle",
    pokedexSortWeight: "Sabse bhari pehle",
    pokedexClearFilters: "Filters saaf karein",
    pokedexTotalEntries: "Kul entries",
    pokedexFilteredEntries: "Matches",
    pokedexPageIndicator: "Page",
    pokedexInspect: "Details dekhen",
    pokedexShowingResults: "{{total}} me se {{start}}-{{end}} dikhaya ja raha hai",
    pokedexOverviewTitle: "अवलोकन",
    pokedexBaseExperience: "बेस अनुभव",
    pokedexAbilitiesTitle: "क्षमताएँ",
    pokedexStatsTitle: "आधार आँकड़े",
    pokedexMovesTitle: "खास चालें",
    pokedexMovesLearnset: "सीखने की सूची",
    pokedexMovesUnavailable: "इस एंट्री के लिए चाल विवरण उपलब्ध नहीं है।",
    pokedexMovesPower: "पावर",
    pokedexMovesAccuracy: "सटीकता",
    pokedexMovesPP: "PP",
    pokedexMovesPriority: "प्राथमिकता",
    pokedexMovesEffect: "प्रभाव",
    pokedexMovesRoutes: "सीखने के तरीके",
    pokedexMovesLevel: "लेवल",
    pokedexMoveMethodLevelUp: "लेवल-अप",
    pokedexMoveMethodMachine: "मशीन",
    pokedexMoveMethodEgg: "एग",
    pokedexMoveMethodTutor: "ट्यूटर",
    pokedexMoveMethodEvolution: "विकास",
    pokedexMoveMethodFormChange: "फॉर्म बदलाव",
    pokedexMoveMethodSpecial: "विशेष",
    pokedexMoveDamagePhysical: "भौतिक",
    pokedexMoveDamageSpecial: "विशेष",
    pokedexMoveDamageStatus: "स्थिति",
    pokedexMovesEffectFallback: "Is move ke liye vartaman bhasha mein effect text uplabdh nahin hai.",
  },
};

